import { cn } from '@quadralize/utils'
import { RepeatOne } from '@solar-icons/react'
import { motion } from 'framer-motion'
import type * as React from 'react'
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '../../overlays/tooltip/tooltip'
import {
  ANIMATE_DURATION,
  EVENT_CARD_HEIGHT_OFFSET,
  EXTRA_TALL_THRESHOLD,
  PAYMENT_DOT,
  PAYMENT_LABEL_PT,
  STATUS_ACCENT,
  STATUS_ACCENT_RECURRING,
  STATUS_BASE,
  STATUS_BASE_RECURRING,
  STATUS_COLOR_CLASS,
  STATUS_LABEL_PT,
  STATUS_OPACITY,
  STATUS_TITLE_DECOR,
  TALL_THRESHOLD,
  VARIANT_ACCENT,
  VARIANT_BASE,
} from './constants'
import type { SchedulerDensity, SchedulerEventRenderContext, SchedulerProps } from './types'
import { formatDuration } from './utils'

interface EventCardProps {
  event: SchedulerEventRenderContext['event']
  top: number
  height: number
  widthPct: number
  leftPct: number
  view: SchedulerEventRenderContext['view']
  activeDate: Date
  resource?: SchedulerEventRenderContext['resource']
  isMobile: boolean
  density?: SchedulerDensity
  onEventClick?: SchedulerProps['onEventClick']
  onEventHover?: SchedulerProps['onEventHover']
  renderEvent?: SchedulerProps['renderEvent']
}

export const EventCard = ({
  event,
  top,
  height,
  widthPct,
  leftPct,
  view,
  activeDate,
  resource,
  isMobile,
  density = 'comfortable',
  onEventClick,
  onEventHover,
  renderEvent,
}: EventCardProps): React.JSX.Element => {
  const isTall = height >= TALL_THRESHOLD
  const isExtraTall = height >= EXTRA_TALL_THRESHOLD
  const isCompact = density === 'compact'
  const isRecurringPending = event.status === 'pending' && event.isRecurring
  const accent = event.status
    ? isRecurringPending
      ? STATUS_ACCENT_RECURRING
      : STATUS_ACCENT[event.status]
    : VARIANT_ACCENT[event.variant ?? 'default']
  const base = event.status
    ? isRecurringPending
      ? STATUS_BASE_RECURRING
      : STATUS_BASE[event.status]
    : VARIANT_BASE[event.variant ?? 'default']
  const duration = formatDuration(event.startTime, event.endTime)
  const statusOpacity = event.status ? STATUS_OPACITY[event.status] : ''
  const titleDecor = event.status ? STATUS_TITLE_DECOR[event.status] : ''
  const customEventContent = renderEvent?.({
    event,
    resource,
    view,
    activeDate,
    isMobile,
  })

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.div
          role="button"
          tabIndex={0}
          aria-label={`${event.title}, ${event.startTime} – ${event.endTime}, ${duration}`}
          initial={{ opacity: 0, y: 2 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.015, y: -1, zIndex: 40 }}
          transition={{ duration: ANIMATE_DURATION }}
          onClick={(e) => {
            e.stopPropagation()
            onEventClick?.(event)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onEventClick?.(event)
            }
          }}
          onMouseEnter={() => onEventHover?.(event)}
          onMouseLeave={() => onEventHover?.(null)}
          className={cn(
            'absolute rounded-md border-l-[3px] overflow-hidden cursor-pointer z-20',
            'flex flex-col shadow-sm ring-1 ring-border-subtle/70 hover:shadow-md transition-[box-shadow,transform]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-1',
            accent,
            base,
            statusOpacity,
            isTall
              ? cn(isCompact ? 'px-1.5 py-1 gap-0.5' : 'px-2 py-1.5 gap-0.5')
              : cn(isCompact ? 'px-1.5 py-0' : 'px-2 py-0', 'justify-center'),
          )}
          style={{
            top: `${top + 2}px`,
            height: `${height - EVENT_CARD_HEIGHT_OFFSET}px`,
            left: `calc(${leftPct}% + 3px)`,
            width: `calc(${widthPct}% - 6px)`,
            ...(event.isRecurring && {
              backgroundImage:
                'repeating-linear-gradient(-45deg, transparent, transparent 8px, rgba(0,0,0,0.04) 8px, rgba(0,0,0,0.04) 9px)',
            }),
          }}
        >
          {/* Payment status dot */}
          {event.paymentStatus && (
            <span
              className={cn(
                'absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full shrink-0 z-10',
                PAYMENT_DOT[event.paymentStatus],
              )}
            />
          )}

          {customEventContent ?? (
            <>
              <div
                className={cn(
                  'flex items-center gap-1 font-semibold leading-tight min-w-0',
                  isCompact ? 'text-[10px]' : 'text-[11px]',
                  titleDecor,
                )}
              >
                {event.isRecurring && (
                  <RepeatOne size={9} weight="Bold" className="shrink-0 opacity-70" />
                )}
                <span className="truncate">{event.title}</span>
              </div>
              {isTall && (
                <div
                  className={cn(
                    'opacity-70 truncate tabular-nums',
                    isCompact ? 'text-[9px]' : 'text-[10px]',
                  )}
                >
                  {event.startTime} – {event.endTime}
                  <span className="opacity-70"> · {duration}</span>
                </div>
              )}
              {isExtraTall && event.description && (
                <div
                  className={cn(
                    'opacity-55 truncate mt-0.5 leading-snug',
                    isCompact ? 'text-[9px]' : 'text-[10px]',
                  )}
                >
                  {event.description}
                </div>
              )}
            </>
          )}
        </motion.div>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-55 flex flex-col gap-0.5">
        <p className={cn('font-semibold text-text-primary', titleDecor)}>{event.title}</p>
        <p className="text-text-secondary tabular-nums">
          {event.startTime} – {event.endTime}
          <span className="ml-1 text-text-tertiary">({duration})</span>
        </p>
        {event.description && (
          <p className="text-text-tertiary text-[11px] pt-0.5 leading-snug">{event.description}</p>
        )}
        {(event.status || event.paymentStatus || event.isRecurring) && (
          <div className="flex items-center gap-2 pt-1 border-t border-border-subtle flex-wrap">
            {event.status && (
              <span className={cn('text-[10px] font-medium', STATUS_COLOR_CLASS[event.status])}>
                {STATUS_LABEL_PT[event.status]}
              </span>
            )}
            {event.paymentStatus && (
              <span className="flex items-center gap-1 text-[10px] text-text-tertiary">
                <span
                  className={cn(
                    'inline-block h-1.5 w-1.5 rounded-full',
                    PAYMENT_DOT[event.paymentStatus],
                  )}
                />
                {PAYMENT_LABEL_PT[event.paymentStatus]}
              </span>
            )}
            {event.isRecurring && (
              <span className="flex items-center gap-1 text-[10px] text-text-tertiary">
                <RepeatOne size={10} weight="Bold" />
                Recorrente
              </span>
            )}
          </div>
        )}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  )
}

EventCard.displayName = 'EventCard'
